import React, { useState, useEffect } from "react";
import api from "../../util/api";
import Footer from "../common/Footer";
import Header from "../common/Header";
import Right from "../common/Right";
import Sidebar from "../common/Sidebar";

const Dashboard = () => {
  const [selectedYear, setSelectedYear] = useState("2026");
  const [selectedMonth, setSelectedMonth] = useState("Full Year");

  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [stats, setStats] = useState({
    volume: 0,
    revenue: 0,
    users: 0,
    totalDeposits: 0,
    totalWithdrawals: 0
  });

  const years = ["2026", "2025", "2024", "2023"];
  const months = ["Full Year", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    calculateStats();
  }, [selectedYear, selectedMonth, deposits, withdrawals, customers]);

  const fetchDashboardData = async () => {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [depRes, withRes, custRes] = await Promise.all([
        api.get("/admin/all-deposits", { headers }),
        api.get("/admin/all-withdrawals", { headers }),
        api.get("/admin/customers", { headers })
      ]);

      if (depRes.data.success) setDeposits(depRes.data.deposits);
      if (withRes.data.success) setWithdrawals(withRes.data.withdrawals);
      if (custRes.data.success) setCustomers(custRes.data.users);
    } catch (err) {
      console.error("Dashboard fetch error", err);
    }
  };

  const calculateStats = () => {
    const filterByDate = (item) => {
      const date = new Date(item.date || item.created_at);
      const itemYear = date.getFullYear().toString();
      const itemMonth = months[date.getMonth() + 1];
      const yearMatch = itemYear === selectedYear;
      const monthMatch = selectedMonth === "Full Year" || itemMonth === selectedMonth;
      return yearMatch && monthMatch;
    };

    const filteredDeps = deposits.filter(filterByDate);
    const filteredWiths = withdrawals.filter(filterByDate);
    const filteredCusts = customers.filter(c => {
      const date = new Date(c.created_at);
      return date.getFullYear().toString() === selectedYear;
    });

    const depSum = filteredDeps.reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0);
    const withSum = filteredWiths.reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0);

    setStats({
      volume: filteredDeps.length + filteredWiths.length,
      revenue: depSum,
      users: filteredCusts.length,
      totalDeposits: depSum,
      totalWithdrawals: withSum
    });
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(val);
  };

  return (
    <div>
      <Header />
      <Sidebar />
      <main id="main" className="main">
        <div className="pagetitle">
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <h1>Dashboard</h1>
              <nav>
                <ol className="breadcrumb">
                  <li className="breadcrumb-item"><a href="/">Home</a></li>
                  <li className="breadcrumb-item active">{selectedMonth} {selectedYear}</li>
                </ol>
              </nav>
            </div>

            <div className="d-flex gap-2 my-2">
              <select className="form-select form-select-sm" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <select className="form-select form-select-sm" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
                {months.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
        </div>

        <section className="section dashboard">
          <div className="row">
            <div className="col-lg-8">
              <div className="row">

                <div className="col-xxl-4 col-md-6">
                  <div className="card info-card sales-card">
                    <div className="card-body">
                      <h5 className="card-title">Volume <span>| {selectedYear}</span></h5>
                      <div className="d-flex align-items-center">
                        <div className="card-icon rounded-circle d-flex align-items-center justify-content-center">
                          <i className="bi bi-grid" />
                        </div>
                        <div className="ps-3">
                          <h6>{stats.volume}</h6>
                          <span className="text-muted small pt-2">Transactions</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xxl-4 col-md-6">
                  <div className="card info-card revenue-card">
                    <div className="card-body">
                      <h5 className="card-title">Total Deposits</h5>
                      <div className="d-flex align-items-center">
                        <div className="card-icon rounded-circle d-flex align-items-center justify-content-center text-success">
                          ₦
                        </div>
                        <div className="ps-3">
                          <h6 className="text-nowrap" style={{ fontSize: '1.2rem' }}>{formatCurrency(stats.totalDeposits)}</h6>
                          <span className="text-muted small pt-2">Inflow</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xxl-4 col-xl-12">
                  <div className="card info-card customers-card">
                    <div className="card-body">
                      <h5 className="card-title">New Users</h5>
                      <div className="d-flex align-items-center">
                        <div className="card-icon rounded-circle d-flex align-items-center justify-content-center">
                          <i className="bi bi-people" />
                        </div>
                        <div className="ps-3">
                          <h6>{stats.users}</h6>
                          <span className="text-muted small pt-2">Registrations</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <div className="card recent-sales overflow-auto">
                    <div className="card-body">
                      <h5 className="card-title">Recent Activity <span>| Deposits & Withdrawals</span></h5>
                      <table className="table table-borderless">
                        <thead>
                          <tr>
                            <th scope="col">Ref</th>
                            <th scope="col">Type</th>
                            <th scope="col">Amount</th>
                            <th scope="col">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deposits.slice(0, 3).map((d, i) => (
                            <tr key={`dep-${i}`}>
                              <td>{d.reference}</td>
                              <td><span className="text-success">Deposit</span></td>
                              <td>{formatCurrency(d.amount)}</td>
                              <td><span className="badge bg-success">{d.status}</span></td>
                            </tr>
                          ))}
                          {withdrawals.slice(0, 3).map((w, i) => (
                            <tr key={`with-${i}`}>
                              <td>{w.reference}</td>
                              <td><span className="text-danger">Withdrawal</span></td>
                              <td>{formatCurrency(w.amount)}</td>
                              <td><span className="badge bg-warning">{w.status}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <div className="card">
                    <div className="card-body">
                      <h5 className="card-title">Net Cashflow <span>| {selectedYear}</span></h5>
                      <div className="alert alert-info d-flex justify-content-between">
                        <span>Net Position:</span>
                        <span className="fw-bold">{formatCurrency(stats.totalDeposits - stats.totalWithdrawals)}</span>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
            <Right />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Dashboard;